import { FatalError, Warning } from '@barthauer/abc/core/error-handling';
import { ObjectHelper } from 'libs/abc/core/helpers';
import { from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';
import { CacheService, DataService, PersistObjectModel, PersistObjectsModel, TargetObjectData } from '../..';
import { CacheStorageAdapter, CacheStorageDataSet } from './cache-storage';
import { CacheDataSet } from './cache.model';

export interface AfterOfflineStorageAction {
  context: any;
  action: (adapter: CachePersistenceAdapter, context: string, data: TargetObjectData[][]) => Promise<void>;
}

export interface AfterOnlineStorageData {
  adapter: CachePersistenceAdapter;
  contexts: {};
  results: any[];
}

interface StoredQueryRow {
  identifier: string;
  context: string;
  query: any;
}

export class CachePersistenceAdapter {
  private static readonly persistObjectsIdentifier: string = 'PersistObjects';
  private static readonly persistObjectIdentifier: string = 'PersistObject';
  private static readonly persistQueriesTableName: string = CacheStorageDataSet.autoIncrementKeyTablePrefix + 'queries';
  public static readonly StateTableName: string = 'state';
  private cacheDataSet: CacheDataSet;
  private cacheStorageDataSet: CacheStorageDataSet;
  private isWaitingForServer: boolean;

  public static readonly CacheDataSetName: string = 'Persistence';

  private readonly storedOnlineSubject = new Subject<AfterOnlineStorageData>();
  public get storedOnline(): Observable<AfterOnlineStorageData> {
    return this.storedOnlineSubject.asObservable();
  }

  constructor(private data: DataService, private cache: CacheService) {
    this.cacheDataSet = new CacheDataSet(CachePersistenceAdapter.CacheDataSetName, null, [
      CachePersistenceAdapter.persistQueriesTableName,
      CachePersistenceAdapter.StateTableName,
    ]);
    this.cache.storage.items.push(this.cacheDataSet);

    setTimeout(async () => {
      if (!CacheStorageAdapter.useOfflineMode && (await this.hasOfflineChanges())) {
        await this.waitToProcessStoredQueries();
      }
    }, CacheStorageAdapter.defaultWaitForOnlineModeIntervalSeconds * 1000);

    CacheStorageAdapter.useOfflineModeChanged.subscribe(async (offline) => {
      if (!offline && (await this.hasOfflineChanges())) {
        await this.waitToProcessStoredQueries();
      }
    });
  }

  public executePersistObjectsQuery(
    persistObjectsModel: PersistObjectsModel,
    acceptOfflineStorage?: () => AfterOfflineStorageAction
  ): Observable<ArrayBuffer> {
    return this.doPersistWithOfflineStorage(
      CachePersistenceAdapter.persistObjectsIdentifier,
      persistObjectsModel,
      acceptOfflineStorage
    );
  }

  public executePersistObjectQuery(
    persistObjectModel: PersistObjectModel,
    acceptOfflineStorage?: () => AfterOfflineStorageAction
  ): Observable<ArrayBuffer> {
    return this.doPersistWithOfflineStorage(
      CachePersistenceAdapter.persistObjectIdentifier,
      persistObjectModel,
      acceptOfflineStorage
    );
  }

  private doPersistWithOfflineStorage(
    identifier: string,
    data: any,
    acceptOfflineStorage: () => AfterOfflineStorageAction
  ): Observable<any> {
    const accept = acceptOfflineStorage ? acceptOfflineStorage() : null;
    return (accept ? from(this.loadState(accept.context)) : of(null)).pipe(
      concatMap((state) => {
        if (state || CacheStorageAdapter.useOfflineMode) {
          return this.doPersistOfflineAndThrowWarning(identifier, data, accept);
        } else {
          return this.doPersistOnline(identifier, data).pipe(
            catchError((err) => {
              if (accept) {
                const errinfo = this.cache.error.getErrorInfo(err);
                const fullMessage = errinfo?.getFullMessage(true)?.toUpperCase();
                if (
                  !fullMessage ||
                  (fullMessage?.indexOf('EXCEPION') < 0 &&
                    fullMessage?.indexOf('RECORDSAFFECTED') < 0 &&
                    fullMessage?.indexOf('_BAMT') < 0 &&
                    fullMessage?.indexOf('_ESGE') < 0)
                ) {
                  return this.doPersistOfflineAndThrowWarning(identifier, data, accept);
                }
              }
              return throwError(err);
            })
          );
        }
      })
    );
  }

  private throwPersistOfflineWarning(): Observable<never> {
    return throwError(new Warning('Daten wurden offline gespeichert!'));
  }

  private throwFatalError(err: any): Observable<never> {
    const errorInfo = this.cache.error.getErrorInfo(err);
    return throwError(new FatalError(errorInfo?.message, errorInfo));
  }

  private doPersistOfflineAndThrowWarning(
    identifier: string,
    data: any,
    accept: AfterOfflineStorageAction
  ): Observable<any> {
    return from(
      this.getStorageDataSet()
        .then(async (s) => {
          if (!accept) {
            throw new Error('AfterOfflineStorageAction is not provided!');
          }
          const row: StoredQueryRow = {
            identifier: identifier,
            context: accept.context,
            query: data,
          };
          try {
            await s.appendValue(row, CachePersistenceAdapter.persistQueriesTableName);
          } catch (err) {
            throw err;
          }
        })
        .then(async () => {
          try {
            if (accept.action) {
              let objectData: TargetObjectData[][];
              if (identifier === CachePersistenceAdapter.persistObjectsIdentifier) {
                objectData = (data as PersistObjectsModel)?.Objects;
              } else if (identifier === CachePersistenceAdapter.persistObjectIdentifier) {
                const o = (data as PersistObjectModel)?.Object;
                if (o) {
                  objectData = [[o]];
                }
              }
              await accept.action(this, accept.context, objectData);
            }
            CacheStorageAdapter.raiseDataChanged();
            await this.waitToProcessStoredQueries();
          } catch (err) {
            throw err;
          }
        })
    ).pipe(
      catchError((err) => this.throwFatalError(err)),
      concatMap((_) => this.throwPersistOfflineWarning())
    );
  }

  private lastSave: number = Date.now();
  private isProcessing: boolean = false;
  private async waitToProcessStoredQueries(): Promise<void> {
    const now = Date.now();
    if (
      !this.isWaitingForServer ||
      (now - this.lastSave) / 1000 > CacheStorageAdapter.defaultWaitForOnlineModeIntervalSeconds * 2
    ) {
      this.lastSave = now;
      await this.cache.storage.waitForOnlineMode(async () => {
        try {
          if (!this.isProcessing) {
            this.isProcessing = true;
            await this.doProcessStoredQueries();
            this.isProcessing = false;
          }
        } catch (err) {
          this.isProcessing = false;
          throw err;
        }
      });
      this.isWaitingForServer = true;
    }
  }

  private processingStoredQueriesCounter = 0;
  private sleep = async (waitTime: number) => new Promise((resolve) => setTimeout(resolve, waitTime));

  public async doProcessStoredQueries(): Promise<void> {
    while (this.processingStoredQueriesCounter > 0) {
      await this.sleep(100);
    }
    this.processingStoredQueriesCounter++;
    try {
      const contexts = {};
      const results = await this.processStoredQueries(contexts);
      this.storedOnlineSubject.next({
        adapter: this,
        contexts: contexts,
        results: results,
      });
      this.isWaitingForServer = false;
    } finally {
      this.processingStoredQueriesCounter--;
    }
  }

  public async getStoredQueries(): Promise<(PersistObjectsModel | PersistObjectModel)[]> {
    const ds = await this.getStorageDataSet();
    const results: PersistObjectsModel | PersistObjectModel[] = await ds.processAllValues(
      async (data) => {
        const row: StoredQueryRow = data;
        let q: PersistObjectsModel | PersistObjectModel;
        if (row.identifier === CachePersistenceAdapter.persistObjectsIdentifier) {
          q = new PersistObjectsModel();
          ObjectHelper.mergeDeepInto(q, row.query);
        } else if (row.identifier === CachePersistenceAdapter.persistObjectIdentifier) {
          q = new PersistObjectModel();
          ObjectHelper.mergeDeepInto(q, row.query);
        }
        return q;
      },
      false,
      CachePersistenceAdapter.persistQueriesTableName
    );
    return results.filter((_) => _);
  }

  private async processStoredQueries(contexts: {}): Promise<any[]> {
    const ds = await this.getStorageDataSet();
    const results = await ds.processAllValues(
      async (data) => await this.processStoredQuery(data, contexts),
      true,
      CachePersistenceAdapter.persistQueriesTableName
    );
    CacheStorageAdapter.raiseDataChanged();
    return results;
  }

  public async deleteStoredQueries(contexts: {}): Promise<number> {
    const ds = await this.getStorageDataSet();
    const result = await ds.processAllValues(
      async (data) => {
        await Promise.resolve();
        contexts[data.context] = (contexts[data.context] ?? 0) + 1;
        return null;
      },
      true,
      CachePersistenceAdapter.persistQueriesTableName
    );
    return result?.length;
  }

  private async processStoredQuery(data: StoredQueryRow, contexts: {}): Promise<any> {
    const result = await this.doPersistOnline(data.identifier, data.query).toPromise();
    contexts[data.context] = (contexts[data.context] ?? 0) + 1;
    return result;
  }

  private doPersistOnline(identifier: string, data: any): Observable<ArrayBuffer> {
    if (identifier === CachePersistenceAdapter.persistObjectsIdentifier) {
      return this.data.executePersistObjectsQuery(data);
    } else if (identifier === CachePersistenceAdapter.persistObjectIdentifier) {
      return this.data.executePersistObjectQuery(data);
    } else {
      throw new Error('Invalid persistance identifier: ' + identifier);
    }
  }

  private async getStorageDataSet(): Promise<CacheStorageDataSet> {
    try {
      if (!(await this.cache.storage.isInCurrentContext(this.cacheStorageDataSet))) {
        this.cacheStorageDataSet = await this.cache.storage.ensureStorageDataSet(this.cacheDataSet);
      }
      return this.cacheStorageDataSet;
    } catch (err) {
      throw err;
    }
  }

  public async saveState(context: string, value: any): Promise<void> {
    const ds = await this.getStorageDataSet();
    await ds?.set(context, value, CachePersistenceAdapter.StateTableName);
  }

  public async loadState(context: string): Promise<any> {
    const ds = await this.getStorageDataSet();
    return await ds?.get(context, CachePersistenceAdapter.StateTableName);
  }

  public async hasOfflineChanges(): Promise<boolean> {
    const ds = await this.getStorageDataSet();
    return await ds?.hasValues(CachePersistenceAdapter.persistQueriesTableName);
  }
}
